import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { renderAddressMapLink } from './reportMapLinks';

const EVENT_LABELS = {
    wire_cutoff: 'Wire Cutoff',
    fake_running: 'Fake Running',
    good_running: 'Good Running',
    loose_connection: 'Loose Connection',
};

const TABLE_EVENT_LABELS = {
    fake_running: 'Running',
    good_running: 'Running',
};

const RUNNING_EVENT_TYPES = new Set(['fake_running', 'good_running']);

const RUNNING_STATUS_LABELS = {
    good: 'Good Running',
    overused: 'Overused',
    suspected_fake: 'Suspected Fake',
    confirmed_fake: 'Confirmed Fake',
    unknown: 'Unknown',
};

const EVENT_FILTERS = [
    { value: 'all', label: 'All Rows' },
    { value: 'event:running', label: 'Running', group: 'Event Type' },
    { value: 'event:wire_cutoff', label: EVENT_LABELS.wire_cutoff, group: 'Event Type' },
    { value: 'event:loose_connection', label: EVENT_LABELS.loose_connection, group: 'Event Type' },
    { value: 'status:good', label: RUNNING_STATUS_LABELS.good, group: 'Running Period Status' },
    { value: 'status:overused', label: RUNNING_STATUS_LABELS.overused, group: 'Running Period Status' },
    { value: 'status:suspected_fake', label: RUNNING_STATUS_LABELS.suspected_fake, group: 'Running Period Status' },
    { value: 'status:confirmed_fake', label: RUNNING_STATUS_LABELS.confirmed_fake, group: 'Running Period Status' },
    { value: 'status:unknown', label: RUNNING_STATUS_LABELS.unknown, group: 'Running Period Status' },
];

const TABLE_COLUMNS = [
    { id: 'eventLabel', label: 'Event' },
    { id: 'assetLabel', label: 'Vehicle' },
    { id: 'generatorSide', label: 'Generator Side' },
    { id: 'sortTime', label: 'Start' },
    { id: 'endTime', label: 'End' },
    { id: 'durationSec', label: 'Duration', align: 'right' },
];

const SUMMARY_ITEMS = [
    { key: 'wireCutoff', label: 'Wire Cutoff' },
    { key: 'fakeRunning', label: 'Fake Running' },
    { key: 'goodRunning', label: 'Good Running' },
    { key: 'looseConnection', label: 'Loose Connection' },
];

const emptyText = '--';

const getAssetLabel = (row) => row?.assetName || row?.vehicleName || row?.imei || emptyText;

const formatHumanLabel = (value) => {
    if (value === undefined || value === null || value === '') return emptyText;
    return String(value)
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (value) => {
    if (!value) return emptyText;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('D/M/YY h:mm a') : value;
};

const getTimeValue = (value) => {
    if (!value) return 0;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

const formatAddress = (value) => {
    if (!value) return emptyText;
    if (typeof value === 'string') return value || emptyText;
    return value.fmtd_addr || value.net_addr || value.address || emptyText;
};

const formatCoordinates = (lat, lng) => {
    if (lat === undefined || lat === null || lng === undefined || lng === null) {
        return emptyText;
    }
    return `${lat}, ${lng}`;
};

const formatSpeed = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `${parsed.toFixed(1)} km/h` : emptyText;
};

const formatVoltage = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `${parsed.toFixed(2)} V` : emptyText;
};

const formatDuration = (durationSec) => {
    const seconds = Number(durationSec);
    if (!Number.isFinite(seconds)) return emptyText;
    const totalMinutes = Math.max(0, Math.round(seconds / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`);
    }
    if (minutes > 0 || parts.length === 0) {
        parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`);
    }

    return parts.join(' ');
};

const compareValues = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    return String(a || '').localeCompare(String(b || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};

const summaryValue = (fleetSummary, key) => ({
    count: Number(fleetSummary?.[key]?.count || 0),
    durationSec: Number(fleetSummary?.[key]?.totalDurationSec || 0),
});

const getSortValue = (row, orderBy) => {
    switch (orderBy) {
        case 'sortTime':
            return row.sortTimeValue;
        case 'endTime':
            return row.endTimeValue;
        case 'durationSec':
            return row.durationValue;
        case 'eventLabel':
            return `${row.tableEventLabel || row.eventLabel} ${row.tableStatusLabel || ''}`;
        case 'assetLabel':
            return row.assetLabel;
        case 'generatorSide':
            return row.generatorSide;
        default:
            return row.sortTimeValue;
    }
};

const getRunningPeriodStatus = (row) => row?.runningPeriodStatus || row?.running_period_status || '';

const getMovementStatus = (row) => (
    row?.movementStatus
    || row?.movement_status
    || row?.movementContext?.movementStatus
    || row?.movementContext?.classification
    || row?.movement_context?.movement_status
    || row?.movement_context?.classification
    || ''
);

const getConfidence = (row) => (
    row?.confidence
    || row?.classificationConfidence
    || row?.classification_confidence
    || ''
);

const getTableEventStatus = (row) => {
    const status = row?.runningPeriodStatus || '';
    if (status) {
        return RUNNING_STATUS_LABELS[status] || formatHumanLabel(status);
    }
    if (row?.eventType === 'good_running') return RUNNING_STATUS_LABELS.good;
    return '';
};

const getTableStatusColor = (status) => {
    switch (status) {
        case 'confirmed_fake':
            return 'error.main';
        case 'suspected_fake':
            return '#ef6c00';
        case 'good':
            return 'success.main';
        default:
            return 'text.secondary';
    }
};

const getTableStatusWeight = (status) => (status === 'confirmed_fake' ? 700 : 400);

const matchesEventFilter = (row, filterValue) => {
    if (filterValue === 'all') return true;
    if (filterValue === 'event:running') return RUNNING_EVENT_TYPES.has(row.eventType);
    if (filterValue.startsWith('event:')) return row.eventType === filterValue.replace('event:', '');
    if (filterValue.startsWith('status:')) return row.runningPeriodStatus === filterValue.replace('status:', '');
    return true;
};

const renderEventFilterOptions = () => EVENT_FILTERS.flatMap((option, index) => {
    const items = [];

    if (option.group && EVENT_FILTERS[index - 1]?.group !== option.group) {
        items.push(
            <MenuItem key={`${option.group}-heading`} disabled>
                <Typography variant="caption" color="textSecondary">
                    {option.group}
                </Typography>
            </MenuItem>
        );
    }

    items.push(
        <MenuItem key={option.value} value={option.value}>
            {option.label}
        </MenuItem>
    );

    return items;
});

const getRationaleBullets = (row) => {
    const bullets = row?.rationaleBullets || row?.ratioalBullets || row?.rationalBullets || [];
    if (Array.isArray(bullets)) {
        return bullets.filter(Boolean);
    }
    return typeof bullets === 'string' && bullets.trim() ? [bullets.trim()] : [];
};

const getTamperTimeline = (row) => {
    const timeline = row?.tamperTimeline || row?.tamperTimeLine || row?.temperTimeLine || row?.timeline || [];
    if (!Array.isArray(timeline)) {
        return [];
    }
    return [...timeline].sort((a, b) => getTimeValue(a?.atTime) - getTimeValue(b?.atTime));
};

const formatTimelineItem = (item) => {
    const zoneChange = [item?.fromZone, item?.toZone].filter(Boolean).join(' -> ');
    const supplyVoltage = formatVoltage(item?.supplyVoltage);
    const genrefVoltage = formatVoltage(item?.genrefVoltage);
    const dropMaxVoltage = formatVoltage(item?.dropMaxVoltage);
    const comparisonValues = [
        supplyVoltage !== emptyText ? `Supply ${supplyVoltage}` : null,
        genrefVoltage !== emptyText ? `Gen ref ${genrefVoltage}` : null,
        dropMaxVoltage !== emptyText ? `Drop max ${dropMaxVoltage}` : null,
    ].filter(Boolean);

    return {
        title: item?.label || item?.step || 'Timeline item',
        time: formatDate(item?.atTime),
        confirmedAt: item?.confirmedAt ? formatDate(item.confirmedAt) : '',
        address: formatAddress(item?.location),
        location: item?.location,
        lat: item?.lat,
        lng: item?.lng,
        voltage: formatVoltage(item?.signalVoltage),
        zoneChange,
        comparisonValues,
        signalField: item?.signalField || '',
        speed: formatSpeed(item?.speedKmph),
        acc: item?.acc !== undefined && item?.acc !== null && item?.acc !== '' ? `ACC ${item.acc}` : '',
        sourceIndex: item?.sourceIndex,
        fromLookback: Boolean(item?.fromLookback),
        coordinates: formatCoordinates(item?.lat, item?.lng),
    };
};

const DetailItem = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2" color="textPrimary">{value || emptyText}</Typography>
    </Grid>
);

const RowAnalysisDetails = ({ row }) => {
    const bullets = getRationaleBullets(row);
    const timeline = getTamperTimeline(row).map(formatTimelineItem);

    if (bullets.length === 0 && timeline.length === 0) {
        return null;
    }

    return (
        <Grid item xs={12}>
            <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
            <Typography variant="subtitle1" color="textPrimary">
                <b>Event Analysis</b>
            </Typography>
            {bullets.length > 0 && (
                <Paper variant="outlined" sx={{ padding: 1, marginTop: 1, backgroundColor: 'White' }}>
                    <Typography variant="body2" color="textSecondary">
                        Rationale
                    </Typography>
                    <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
                        {bullets.map((bullet, index) => (
                            <li key={`${bullet}-${index}`} style={{ marginBottom: 2 }}>
                                <Typography variant="body2" color="textPrimary">
                                    {bullet}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </Paper>
            )}
            {timeline.length > 0 && (
                <Paper variant="outlined" sx={{ padding: 1, marginTop: 1, backgroundColor: 'White' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 0.5 }}>
                        Timeline
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Step</b></TableCell>
                                    <TableCell><b>Signal</b></TableCell>
                                    <TableCell><b>Zone</b></TableCell>
                                    <TableCell><b>Context</b></TableCell>
                                    <TableCell><b>Location</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {timeline.map((item, index) => (
                                    <TableRow key={`${item.title}-${item.time}-${index}`}>
                                        <TableCell sx={{ py: 0.75, minWidth: 210 }}>
                                            <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                {index + 1}. {item.title}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                {item.time}{item.confirmedAt ? ` | Confirmed ${item.confirmedAt}` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, minWidth: 110 }}>
                                            <Typography variant="body2" color="textPrimary">
                                                {[item.signalField, item.voltage].filter(Boolean).join(' | ') || emptyText}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, minWidth: 120 }}>
                                            <Typography variant="body2" color="textPrimary">
                                                {item.zoneChange || emptyText}
                                            </Typography>
                                            {item.comparisonValues.length > 0 && (
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                    {item.comparisonValues.join(' | ')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, minWidth: 120 }}>
                                            <Typography variant="body2" color="textPrimary">
                                                {[item.speed, item.acc].filter((value) => value && value !== emptyText).join(' | ') || emptyText}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                {[
                                                    item.sourceIndex !== undefined ? `Index ${item.sourceIndex}` : '',
                                                    item.fromLookback ? 'Lookback' : '',
                                                ].filter(Boolean).join(' | ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, minWidth: 180 }}>
                                            <Typography variant="body2" color="textPrimary" sx={{ lineHeight: 1.25 }}>
                                                {renderAddressMapLink({
                                                    address: item.location,
                                                    text: item.address,
                                                    lat: item.lat,
                                                    lng: item.lng,
                                                    source: item,
                                                })}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                {item.coordinates}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Grid>
    );
};

const TimeAddressCell = ({ time, address, addressSource, source, type }) => (
    <>
        <Typography variant="body2" color="textPrimary">{time}</Typography>
        <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: 'block', maxWidth: 220, whiteSpace: 'normal', lineHeight: 1.25 }}
        >
            {renderAddressMapLink({
                address: addressSource,
                text: address,
                source,
                type,
            })}
        </Typography>
    </>
);

const GensetTamper = ({ reportData }) => {
    const dimensions = useSelector(liveTrackinglayoutSelector);
    const [eventFilter, setEventFilter] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [orderBy, setOrderBy] = useState('sortTime');
    const [order, setOrder] = useState('asc');
    const [selectedRow, setSelectedRow] = useState(null);
    const [skippedOpen, setSkippedOpen] = useState(false);

    const rows = useMemo(() => Array.isArray(reportData?.rows) ? reportData.rows : [], [reportData]);
    const skippedRows = useMemo(() => Array.isArray(reportData?.skipped) ? reportData.skipped : [], [reportData]);
    const fleet = reportData?.fleet || {};
    const fleetSummary = fleet?.summary || {};

    const normalizedRows = useMemo(() => rows.map((row, index) => {
        const eventType = row?.eventType || '';
        const eventLabel = row?.eventLabel || EVENT_LABELS[eventType] || formatHumanLabel(eventType);
        const tableEventLabel = TABLE_EVENT_LABELS[eventType] || eventLabel;
        const runningPeriodStatus = getRunningPeriodStatus(row);
        const runningPeriodStatusLabel = runningPeriodStatus ? RUNNING_STATUS_LABELS[runningPeriodStatus] || formatHumanLabel(runningPeriodStatus) : '';
        const tableStatusKey = runningPeriodStatus || (eventType === 'good_running' ? 'good' : '');
        const tableStatusLabel = getTableEventStatus({ ...row, eventType, runningPeriodStatus });
        const movementStatus = getMovementStatus(row);
        const movementStatusLabel = movementStatus ? formatHumanLabel(movementStatus) : '';
        const confidence = getConfidence(row);
        const confidenceLabel = confidence ? formatHumanLabel(confidence) : '';
        const assetLabel = getAssetLabel(row);
        const generatorSide = row?.generatorSide || row?.sideKey || emptyText;
        const startAddressSource = row?.startLocation || row?.startAddress;
        const endAddressSource = row?.endLocation || row?.endAddress;
        const startAddress = formatAddress(startAddressSource);
        const endAddress = formatAddress(endAddressSource);
        const startDisplay = formatDate(row?.startTime);
        const endDisplay = formatDate(row?.endTime);
        const durationDisplay = formatDuration(row?.durationSec);
        const normalized = {
            ...row,
            rowKey: row?.rowId || `${row?.imei || 'row'}-${row?.sideKey || 'side'}-${eventType}-${index}`,
            eventType,
            eventLabel,
            tableEventLabel,
            runningPeriodStatus,
            runningPeriodStatusLabel,
            tableStatusKey,
            tableStatusLabel,
            movementStatus,
            movementStatusLabel,
            confidence,
            confidenceLabel,
            assetLabel,
            generatorSide,
            startDisplay,
            endDisplay,
            startAddressSource,
            endAddressSource,
            startAddress,
            endAddress,
            durationDisplay,
            sortTimeValue: getTimeValue(row?.sortTime || row?.startTime),
            endTimeValue: getTimeValue(row?.endTime),
            durationValue: Number(row?.durationSec || 0),
        };
        normalized.searchText = [
            normalized.eventLabel,
            normalized.tableEventLabel,
            normalized.runningPeriodStatusLabel,
            normalized.tableStatusLabel,
            normalized.movementStatusLabel,
            normalized.confidenceLabel,
            normalized.assetLabel,
            normalized.generatorSide,
            normalized.imei,
            normalized.signalField,
            normalized.startAddress,
            normalized.endAddress,
        ].filter(Boolean).join(' ').toLowerCase();
        return normalized;
    }), [rows]);

    const tableRows = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        const filtered = normalizedRows.filter((row) => {
            const eventMatch = matchesEventFilter(row, eventFilter);
            const searchMatch = !query || row.searchText.includes(query);
            return eventMatch && searchMatch;
        });

        return filtered.sort((a, b) => {
            const primary = compareValues(getSortValue(a, orderBy), getSortValue(b, orderBy));
            const direction = order === 'asc' ? primary : -primary;
            if (direction !== 0) return direction;

            return compareValues(a.assetLabel, b.assetLabel)
                || compareValues(a.generatorSide, b.generatorSide)
                || compareValues(a.eventLabel, b.eventLabel);
        });
    }, [eventFilter, normalizedRows, order, orderBy, searchText]);

    const handleSort = (columnId) => {
        const isCurrent = orderBy === columnId;
        setOrder(isCurrent && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const summaryCards = SUMMARY_ITEMS.map((item) => ({
        ...item,
        ...summaryValue(fleetSummary, item.key),
    }));

    if (!reportData) return null;

    return (
        <Paper style={{ ...dimensions.reportDetailViewBottom, overflow: 'auto' }}>
            <div style={{ margin: '10px' }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Card variant="outlined" style={{ backgroundColor: 'White' }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="stretch">
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="body2" color="textSecondary">
                                            Vehicles with Genset Tamper Monitoring
                                        </Typography>
                                        <Typography variant="h6" color="textPrimary">
                                            <b>{Number(fleet?.processedVehicleCount || 0).toLocaleString('en-IN')}</b>
                                        </Typography>
                                    </Grid>
                                    {summaryCards.map((item) => (
                                        <Grid item xs={12} sm={6} md={3} key={item.key}>
                                            <Typography variant="body2" color="textSecondary">{item.label}</Typography>
                                            <Typography variant="h6" color="textPrimary">
                                                <b>{item.count.toLocaleString('en-IN')}</b>
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {formatDuration(item.durationSec)}
                                            </Typography>
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="textSecondary">Fleet Vehicles</Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            <b>{Number(fleet?.vehicleCount || 0).toLocaleString('en-IN')}</b>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="textSecondary">Events</Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            <b>{rows.length.toLocaleString('en-IN')}</b>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="textSecondary">Skipped Vehicles</Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={skippedRows.length === 0}
                                            onClick={() => setSkippedOpen(true)}
                                            sx={{ marginTop: '2px' }}
                                        >
                                            {Number(fleet?.skippedVehicleCount || skippedRows.length || 0).toLocaleString('en-IN')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" style={{ padding: '10px', backgroundColor: 'White' }}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} sm={4} md={3}>
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="genset-tamper-event-filter">Event / Status</InputLabel>
                                        <Select
                                            labelId="genset-tamper-event-filter"
                                            value={eventFilter}
                                            onChange={(event) => setEventFilter(event.target.value)}
                                        >
                                            {renderEventFilterOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={8} md={5}>
                                    <TextField
                                        variant="standard"
                                        fullWidth
                                        label="Filter"
                                        value={searchText}
                                        onChange={(event) => setSearchText(event.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="textSecondary">
                                        Showing <b>{tableRows.length.toLocaleString('en-IN')}</b> of <b>{rows.length.toLocaleString('en-IN')}</b>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {TABLE_COLUMNS.map((column) => (
                                            <TableCell key={column.id} align={column.align || 'left'}>
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleSort(column.id)}
                                                >
                                                    <b>{column.label}</b>
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows.map((row) => (
                                        <TableRow
                                            key={row.rowKey}
                                            hover
                                            onClick={() => setSelectedRow(row)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                    {row.tableEventLabel}
                                                </Typography>
                                                {row.tableStatusLabel && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: getTableStatusColor(row.tableStatusKey),
                                                            display: 'block',
                                                            fontWeight: getTableStatusWeight(row.tableStatusKey),
                                                            lineHeight: 1.2,
                                                            marginTop: 0.25,
                                                        }}
                                                    >
                                                        {row.tableStatusLabel}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textPrimary">{row.assetLabel}</Typography>
                                                <Typography variant="caption" color="textSecondary">{row.imei || emptyText}</Typography>
                                            </TableCell>
                                            <TableCell>{row.generatorSide}</TableCell>
                                            <TableCell>
                                                <TimeAddressCell
                                                    time={row.startDisplay}
                                                    address={row.startAddress}
                                                    addressSource={row.startAddressSource}
                                                    source={row}
                                                    type="start"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TimeAddressCell
                                                    time={row.endDisplay}
                                                    address={row.endAddress}
                                                    addressSource={row.endAddressSource}
                                                    source={row}
                                                    type="end"
                                                />
                                            </TableCell>
                                            <TableCell align="right">{row.durationDisplay}</TableCell>
                                        </TableRow>
                                    ))}
                                    {tableRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={TABLE_COLUMNS.length}>
                                                <Typography variant="body2" color="textSecondary">
                                                    No genset tamper rows found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </div>

            <Dialog
                open={Boolean(selectedRow)}
                onClose={() => setSelectedRow(null)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{ sx: { maxHeight: '90vh' } }}
            >
                <DialogTitle>
                    <b>{selectedRow?.eventLabel || 'Genset Tamper Event'}</b>
                </DialogTitle>
                <DialogContent dividers sx={{ overflowY: 'auto' }}>
                    <Grid container spacing={2}>
                        <DetailItem label="Vehicle" value={selectedRow?.assetLabel} />
                        <DetailItem label="Generator Side" value={selectedRow?.generatorSide} />
                        <DetailItem label="Start Time" value={selectedRow?.startDisplay} />
                        <DetailItem label="End Time" value={selectedRow?.endDisplay} />
                        <DetailItem label="Duration" value={selectedRow?.durationDisplay} />
                        <DetailItem label="Running Period Status" value={selectedRow?.runningPeriodStatusLabel} />
                        <DetailItem label="Movement Status" value={selectedRow?.movementStatusLabel} />
                        <DetailItem label="Confidence" value={selectedRow?.confidenceLabel} />
                        <DetailItem label="Status" value={selectedRow?.isOpen ? 'Open' : 'Closed'} />
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <DetailItem
                            label="Start Address"
                            value={renderAddressMapLink({
                                address: selectedRow?.startAddressSource,
                                text: selectedRow?.startAddress,
                                source: selectedRow,
                                type: 'start',
                            })}
                        />
                        <DetailItem
                            label="End Address"
                            value={renderAddressMapLink({
                                address: selectedRow?.endAddressSource,
                                text: selectedRow?.endAddress,
                                source: selectedRow,
                                type: 'end',
                            })}
                        />
                        <DetailItem label="Start Coordinates" value={formatCoordinates(selectedRow?.startLat, selectedRow?.startLng)} />
                        <DetailItem label="End Coordinates" value={formatCoordinates(selectedRow?.endLat, selectedRow?.endLng)} />
                        <RowAnalysisDetails row={selectedRow} />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedRow(null)} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={skippedOpen} onClose={() => setSkippedOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle><b>Skipped Vehicles</b></DialogTitle>
                <DialogContent dividers>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Vehicle</b></TableCell>
                                    <TableCell><b>Message</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {skippedRows.map((item, index) => (
                                    <TableRow key={`${item?.imei || 'skipped'}-${index}`}>
                                        <TableCell>{item?.assetName || item?.vehicleName || item?.imei || emptyText}</TableCell>
                                        <TableCell>{item?.message || item?.reason || emptyText}</TableCell>
                                    </TableRow>
                                ))}
                                {skippedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <Typography variant="body2" color="textSecondary">
                                                No skipped vehicles.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSkippedOpen(false)} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default GensetTamper;
